/**
 * Spec §1.2 — ONE surface for every operation on the relationship facts behind a chip.
 *
 * **Why a modal at all**, when P4 deliberately chose an inline control: §0 case 3 measured why the row
 * could not carry what the data needs. An inline `<select>` on a chip row cannot express a two-qualifier
 * fact (`transaction_advisor` needs discipline AND side), cannot disambiguate an entity that holds
 * several facts of different types, cannot change a fact's TYPE, and has nowhere to put the mandatory
 * reason on a removal. Owner directive, 2026-08-06: *"edits should happen via a modal accessed via a
 * pencil edit icon on the row."*
 *
 * **One fact per section, one Save per section.** Each fact is one ledger command; a single Save across
 * several would make one failure ambiguous about what landed. So an entity that is both the target and
 * its own adviser is two sections here, each editable on its own — which is what structurally fixes the
 * chip that today offers no control at all because its facts span two types.
 *
 * **The picker is injected, not imported.** `renderRefPicker` is a render prop because the governed ref
 * editor mounts `RefCellEditor`, which queries the directory and needs a `QueryClient`. Injecting it
 * keeps this component — the one with all the branching logic — testable without a provider, and closes
 * the documented gap where "the panel test suite cannot open the add picker".
 *
 * NOT deep-linked: a transient modal is `useState` only (`.claude/rules/frontend.md`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  CrmFactInterpretResponse, CrmProjectedAddOptions, CrmProjectedAddSlot, CrmProjectedAddType,
  CrmProjectedChip, CrmProjectedFact,
} from '../../types';
import { FIELD_REASONS } from '../pages/DismissReasonModal';
import { FactWhyButton } from './CrmFactEvidencePopover';
import { formatRole } from './crmEntityRoles';
import styles from './crm.module.css';

/** The qualifier values a section currently holds, keyed by qualifier key. */
type Draft = Record<string, string>;

/**
 * §1.3's natural-language front door, injected the same way the picker is — so this component stays
 * testable without a QueryClient, and so the modal has no opinion about how the call is made.
 *
 * ⚠ `run` reaches a READ-ONLY endpoint. It returns a draft; the write still happens through `onAddFact`,
 * i.e. the same `facts/add` the slot controls use, with every gate already on it. §0: "this does not
 * replace the slot controls — the model's answer renders IN them. They become the confirm surface, and
 * the fallback when the model is unavailable." Absent ⇒ the controls alone, which is that fallback.
 */
export interface DescribeIt {
  run: (args: { text: string; priorProposal?: unknown; answer?: string },
        onProposal: (r: CrmFactInterpretResponse) => void) => void;
  busy?: boolean;
  error?: string | null;
}

interface Props {
  /** Column label, for the dialog's own labelling — never a substitute for the entity name. */
  columnLabel: string;
  /** The record this fact hangs off (the deal), so the title says what is being edited and where. */
  entryLabel: string;
  /** The chip the pencil was clicked on, or null for the field-level `+ Add` (add-only surface). */
  chip: CrmProjectedChip | null;
  /** The column's admissible types and their ordered slots. Absent ⇒ nothing can be added here. */
  addOptions?: CrmProjectedAddOptions;
  busy?: boolean;
  /** Surfaced INSIDE the card: a page-flow banner renders behind the backdrop (prior adv review). */
  error?: string | null;
  onClose: () => void;
  /** Role/descriptor change on an existing fact, same type. Sends ONLY the keys that changed. */
  onSaveFact: (fact: CrmProjectedFact, qualifiers: Draft) => void;
  /** Type change — a refute + a new predecessor-linked fact, not an edit (`fact_key` carries the type). */
  onRetypeFact: (fact: CrmProjectedFact, relationshipType: string, qualifiers: Draft) => void;
  onChangeEntity: (fact: CrmProjectedFact, localId: number) => void;
  /** Removal with its mandatory reason — the highest-value tuning row, never optional. */
  onRemoveFact: (fact: CrmProjectedFact, reason: string) => void;
  /**
   * Confirm a machine-derived fact (a provenance upgrade, not a value change).
   *
   * ⚠ It lives HERE because §1.1 strips the row back to one pencil, and §1.2 is "one surface, every
   * operation". Dropping the row's ✓ without rehoming it would have deleted a shipped capability under
   * cover of a UI rework — the confirmation is the corroboration signal E5a is meant to weight.
   */
  onConfirmFact: (fact: CrmProjectedFact) => void;
  /** `reason` carries the operator's own sentence when the add came from the box (§2). */
  onAddFact: (relationshipType: string, localId: number, qualifiers: Draft,
              reason?: string) => void;
  /** §1.3's describe-it box. Absent ⇒ the slot controls alone — the model-unavailable fallback. */
  describe?: DescribeIt;
  /** The governed ref picker, injected. `initial: null` ⇒ an add names a new entity. */
  renderRefPicker: (args: {
    initial: unknown; onCommit: (v: unknown) => void; onCancel: () => void;
  }) => React.ReactNode;
  /**
   * WP-6b — a governed ORGANISATION picker, for `acting_for`.
   *
   * ⚠ A SECOND render prop rather than a reuse of `renderRefPicker`, and that is the point: that one
   * is scoped to the COLUMN's own kind (a `linked_people` column gives a person picker), while the
   * capacity is always a firm. Reusing it would offer a person picker for "which firm did they act
   * through" on exactly the columns where the question arises.
   *
   * Optional: absent ⇒ the capacity renders READ-ONLY, exactly as PR-C shipped it. A surface that
   * cannot mount the governed picker must not grow an editor that writes an id it never gated.
   */
  renderOrgPicker?: (args: {
    initial: unknown; onCommit: (v: unknown) => void; onCancel: () => void;
  }) => React.ReactNode;
}

/** The slots of a type, or `[]` when the column does not admit it (a fact of a type v1 cannot render). */
function slotsFor(addOptions: CrmProjectedAddOptions | undefined, type: string): CrmProjectedAddSlot[] {
  return addOptions?.types.find((t) => t.relationship_type === type)?.slots ?? [];
}

function typeLabel(addOptions: CrmProjectedAddOptions | undefined, type: string): string {
  return addOptions?.types.find((t) => t.relationship_type === type)?.label ?? formatRole(type);
}

/**
 * "The operator has not answered this slot yet" — DISTINCT from `''`, which is the answer *(not
 * stated)*. Never sent: `payloadFor`/`save` drop it, and no control can be committed while it is set.
 *
 * Double-underscore-bracketed so it cannot be mistaken for a vocabulary value: those are lower-case
 * slugs from `controlled_vocabulary.value`, and the sentinel option is rendered only while the draft
 * actually holds it, so it can never appear alongside a real one.
 */
const UNCHOSEN = '__unchosen__';

/**
 * Does this slot demand an ANSWER before the fact may be written, even though the ledger would accept it
 * empty?
 *
 * ⛔ **OWNER RULING 2026-08-10 — NO SILENT DEFAULT ON AN OPTIONAL IDENTITY-BEARING SLOT.** D1 made
 * `transaction_side` optional-and-identity-bearing, and this form then defaulted it to *(not stated)*:
 * an operator who tabbed past minted a side-less fact by doing nothing. That is not a cheap mistake.
 * The side is inside `fact_key`, so adding it later is a refute-and-recreate rather than an edit; and a
 * side-less fact a HUMAN decided is beyond the reconciler's reach, so nothing will ever fold it — the
 * one D1 hazard the ruling's other half (retiring `transaction_side='unknown'`) does not close.
 *
 * So the slot starts UNCHOSEN and the commit is blocked until the operator picks a side or picks *(not
 * stated)* on purpose. Both answers stay available — this buys a decision, not a value.
 *
 * REQUIRED slots are not in scope here: they already block on `''`. A `fixed_value` slot is not either —
 * the column has already answered it, `payloadFor` sends the pin regardless of the draft, and on the add
 * path no control is even rendered, so forcing a choice there would be a permanently unanswerable block.
 * ⚠ That is the ONE place `seedDraft` transitively consults `fixed_value`, against its own docstring's
 * "NEVER consults" rule — narrowly, and only to avoid demanding an answer to a question already settled.
 */
function forcesChoice(slot: CrmProjectedAddSlot): boolean {
  return !slot.required && slot.identity_bearing && slot.control === 'controlled' && !slot.fixed_value;
}

/**
 * Slots the operator still owes an answer on. Blocks Save/Add — never silently coerced to a value.
 *
 * ⛔ **IT ASKS THE SLOT, NOT THE DRAFT, WHEN THE DRAFT HAS NO ENTRY — a sentinel-presence test FAILS
 * OPEN.** `draft` is seeded once, in a `useState` initializer, from the slots available at mount; a
 * slot that is not in it yet (`addOptions` still resolving, or a slot the server added to a form
 * already open) would then read as ANSWERED, and `SlotControl`'s `draft[s.key] ?? ''` renders it as
 * *(not stated)* pre-selected — precisely the silent default the ruling forbids, arriving through a
 * render-timing accident rather than a decision. Keying the missing case off `forcesChoice` makes the
 * guard a property of the SLOT, so it cannot depend on which render won.
 */
function unanswered(slots: CrmProjectedAddSlot[], draft: Draft): boolean {
  return slots.some((s) => (s.key in draft ? draft[s.key] === UNCHOSEN : forcesChoice(s)));
}

/** A value that goes on the wire: `''` (not stated) and UNCHOSEN are both "send nothing". */
function isStated(value: string | undefined): boolean {
  return value !== undefined && value !== '' && value !== UNCHOSEN;
}

/**
 * Seed a draft from a type's slots.
 *
 * ⚠ It NEVER consults `fixed_value`. That is the column's ADD-time pin — "adding to Organizations means
 * adding the target" — which is right when creating a fact and exactly backwards when correcting one:
 * moving the fact out of that column is the correction being made. Honouring the pin while editing left
 * the three role-pinned columns (`organizations`, `seller_orgs`, `add_on_buyer_portco`) with no role
 * control at all, having just removed the inline `<select>` that did work there. `SlotControl`'s own
 * `pinned` flag is the single place the pin is applied, and only the add path passes it.
 *
 * ⚠ It is also the ONE seam where the force-choice ruling is applied, so every path that fills the form
 * inherits it: the add form, a type change, a retype of an existing fact, and the describe-it proposal.
 * A slot the SOURCE already answers keeps that answer (an existing fact's own side, a side the model
 * read out of the sentence); a slot it says nothing about starts UNCHOSEN rather than *(not stated)*,
 * because the model's silence and the fact's absence are not the operator's decision.
 */
function seedDraft(slots: CrmProjectedAddSlot[], existing: Draft): Draft {
  const out: Draft = {};
  for (const s of slots) out[s.key] = existing[s.key] ?? (forcesChoice(s) ? UNCHOSEN : '');
  return out;
}

/** One control per slot. `controlled` → a select over ACTIVE values; `free_text` → an input. */
function SlotControl(
  { slot, value, disabled, onChange, idPrefix, pinned }:
  { slot: CrmProjectedAddSlot; value: string; disabled?: boolean;
    onChange: (v: string) => void; idPrefix: string;
    /** ADD only — see `seedDraft`. An edit must always be able to change the value. */
    pinned?: boolean },
) {
  const label = formatRole(slot.key);
  const id = `${idPrefix}-${slot.key}`;
  if (pinned && slot.fixed_value) {
    // Stated so the operator can see what the new fact will say, not offered as a choice: a different
    // value would make it invisible in the very column it was added from.
    return (
      <div className={styles.factSlot}>
        <span className={styles.factSlotLabel}>{label}</span>
        <span className={styles.factSlotFixed}>{formatRole(slot.fixed_value)}</span>
      </div>
    );
  }
  if (slot.control === 'free_text') {
    return (
      <div className={styles.factSlot}>
        <label className={styles.factSlotLabel} htmlFor={id}>{label}</label>
        <input id={id} className={styles.modalInput} maxLength={400} value={value} disabled={disabled}
               onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  // ⚠ A RETIRED current value stays visible and selected. An inactive vocabulary row must not be
  // offered for a NEW choice — the server already excludes it from `values` — but it must still
  // describe the existing fact honestly rather than being silently dropped to blank (§1.2). This is
  // what a pre-2026-08-10 `transaction_side='unknown'` renders as, now that the value is retired.
  // ⚠ DISPLAY ONLY on a retired IDENTITY-BEARING value: `supersede_with_manual` MERGES the unsent key
  // with the stored row, so a Save that leaves it selected re-submits it and the ledger refuses it as
  // inactive (a 400 with nothing on screen explaining it). Prod carries zero such rows, so the path is
  // unreachable today; it is recorded rather than fixed because forcing a replacement is a UX decision.
  const unchosen = value === UNCHOSEN;
  const retired = value !== '' && !unchosen && !slot.values.some((v) => v.value === value);
  return (
    <div className={styles.factSlot}>
      <label className={styles.factSlotLabel} htmlFor={id}>{label}</label>
      <select id={id} className={styles.recordRoleSelect} value={value} disabled={disabled}
              onChange={(e) => onChange(e.target.value)}>
        {/* ⛔ THE UNANSWERED STATE IS ITS OWN OPTION, not a re-use of the blank. "(not stated)" is an
            ANSWER — it says the side is deliberately absent — and while it doubled as the initial
            value there was no way to tell a decision from a default, which is what the 2026-08-10
            ruling is about. Selecting it is what removes this option and unblocks the commit. */}
        {unchosen && <option value={UNCHOSEN}>Choose…</option>}
        <option value="">{slot.required ? 'Choose…' : '(not stated)'}</option>
        {retired && <option value={value}>{formatRole(value)} (retired)</option>}
        {slot.values.map((v) => (
          <option key={v.value} value={v.value} title={v.description_terse ?? undefined}>{v.label}</option>
        ))}
      </select>
    </div>
  );
}

/** One existing fact: its type, its slots, its capacity, Remove-with-reason, and its own Save. */
function FactSection(
  { fact, addOptions, busy, onSave, onRetype, onRemove, onConfirm, onChangeEntityClick,
    renderOrgPicker }:
  { fact: CrmProjectedFact; addOptions?: CrmProjectedAddOptions; busy?: boolean;
    onSave: (q: Draft) => void; onRetype: (type: string, q: Draft) => void;
    onRemove: (reason: string) => void; onConfirm: () => void; onChangeEntityClick: () => void;
    renderOrgPicker?: Props['renderOrgPicker'] },
) {
  const [type, setType] = useState(fact.relationship_type);
  // WP-6b — the capacity the operator has PICKED in this session. `undefined` is "untouched", which
  // is a different statement from "no firm": untouched sends nothing and the ledger's merge keeps
  // whatever the fact already says.
  const [capacity, setCapacity] = useState<{ local_id: number; name: string | null } | undefined>();
  const [pickingOrg, setPickingOrg] = useState(false);
  const [draft, setDraft] = useState<Draft>(
    () => seedDraft(slotsFor(addOptions, fact.relationship_type), fact.qualifiers));
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  // Machine-produced ⇒ the removal owes a reason; the operator's own entry does not.
  const reasonOwed = fact.producer_kind !== 'manual';
  // No arbitrary minimum length: the inherited "at least 5 characters" rule silently rejected a real
  // answer ("test") with the button greyed out and nothing saying why.
  const canRemove = !reasonOwed
    ? (!reason || reason !== 'other' || !!note.trim())
    : (!!reason && (reason !== 'other' || !!note.trim()));
  const removeHint = canRemove ? undefined
    : reason === 'other' ? 'Say more, since the reason is “Other”'
    : 'Choose why it is being removed — this one came from the AI';

  const slots = slotsFor(addOptions, type);
  const retyping = type !== fact.relationship_type;
  // A fact whose own type the column cannot render (a required qualifier needing a control §10 defers)
  // has no slots. It is shown read-only rather than hidden: the operator must be able to SEE a fact
  // that exists, and can still remove it or retype it to something renderable.
  const unrenderable = slots.length === 0 && !retyping;

  const changeType = (next: string) => {
    setType(next);
    // The role vocabularies are disjoint per type, so those keys cannot carry over — but the
    // DESCRIPTIVE ones can, and the server carries them anyway on a retype. Re-seeded from what the
    // fact holds so a descriptor the operator can see is the descriptor that gets written.
    setDraft(seedDraft(slotsFor(addOptions, next), fact.qualifiers));
  };

  const missingRequired = slots.some((s) => s.required && !draft[s.key]);
  // ⛔ An optional identity-bearing slot the fact does not already answer starts UNCHOSEN, so a SAVE
  // cannot re-mint the fact with a side-less default the operator never looked at. Every save here is
  // a refute-and-recreate (`identityChanged` below is required for the command to be accepted at all),
  // so this is a mint path and the 2026-08-10 ruling applies to it exactly as it does to the add form.
  const owesChoice = unanswered(slots, draft);
  /** Keys the operator actually altered, ignoring a blank or an unanswered slot (see `save`). */
  const changedKeys = slots
    .filter((s) => isStated(draft[s.key]) && (draft[s.key] ?? '') !== (fact.qualifiers[s.key] ?? ''))
    .map((s) => s.key);
  // ⚠ An EDIT must alter an IDENTITY-BEARING qualifier or the command refuses it outright:
  // `supersede_with_manual` is refute-old + create-corrected, which is only correct when something
  // inside `fact_key` moved, and it says so ("that change does not alter the fact's identity, so it
  // cannot be applied as a correction"). Without this check Save was enabled for a descriptor-only
  // change and every such Save was a guaranteed 400 — on the role-pinned columns it was the ONLY
  // enabled control. `identity_bearing` is on the wire for exactly this.
  // ⚠ `acting_for` is IDENTITY-BEARING and is NOT a slot — `_RENDERABLE_SLOT_KINDS` covers controlled
  // values and free text, and an entity ref is neither, which is why PR-C could only render it
  // read-only. So the capacity has to be OR'd in here explicitly: without it Save stays disabled for
  // a capacity-only correction, which is the one this control exists to make.
  const capacityChanged = capacity != null && capacity.local_id !== (fact.acting_for?.local_id ?? null);
  const identityChanged = slots.some((s) => s.identity_bearing && changedKeys.includes(s.key))
    || capacityChanged;
  const canSave = !busy && !unrenderable && !missingRequired && !owesChoice
    && (retyping || identityChanged);
  const saveHint = missingRequired ? 'Fill in every required field first'
    : owesChoice ? 'Choose a value, or “(not stated)”, for every field first'
    : !canSave && !busy && !unrenderable && changedKeys.length > 0 && !identityChanged
      ? 'Descriptive detail can only be set when the relationship is created, not changed afterwards'
      : undefined;

  const save = () => {
    if (!canSave) return;
    // Only the keys carrying a value, and on an edit only the ones that CHANGED: the ledger MERGES an
    // edit with the stored row, so an unchanged key is noise.
    //
    // ⚠ A blank is skipped rather than sent, and that is a real limitation rather than a nicety: there
    // is no way to CLEAR a qualifier through these commands, so sending '' would either be refused or
    // silently ignored by the merge. Blanks are excluded from `changedKeys` too, so emptying a field
    // cannot look like a pending change that then vanishes on save.
    const payload: Draft = {};
    for (const s of slots) {
      const v = draft[s.key] ?? '';
      if (!isStated(v)) continue;              // '' is "(not stated)"; UNCHOSEN cannot reach here
      if (!retyping && v === (fact.qualifiers[s.key] ?? '')) continue;
      payload[s.key] = v;
    }
    // The canonical ENTITY_REF form the ledger's normalizer writes (`identity._norm_entity_ref`), so
    // the value the picker produced and the value already on the fact are the same string — a bare
    // id would land a shape `deal_field_projection._acting_for_org` only learned to read after F-6.
    // Sent only when it MOVED, because the command merges with the stored row.
    if (capacityChanged && capacity) payload.acting_for = `organization:${capacity.local_id}`;
    if (retyping) onRetype(type, payload); else onSave(payload);
  };

  return (
    <section className={styles.factSection}
             aria-label={`${typeLabel(addOptions, fact.relationship_type)} — record ${fact.assertion_id}`}>
      {/* WP-6b — the evidence behind THIS fact, beside the controls that change it. The operator
          about to correct a role is exactly the person who wants to see what produced it, and this
          is the one surface where the two questions are a click apart. */}
      <div className={styles.factSlot}>
        <span className={styles.factSlotLabel}>Evidence</span>
        <FactWhyButton assertionIds={[fact.assertion_id]}
                       label={typeLabel(addOptions, fact.relationship_type)} />
      </div>
      <div className={styles.factSlot}>
        <label className={styles.factSlotLabel} htmlFor={`fact-${fact.assertion_id}-type`}>
          Relationship
        </label>
        <select id={`fact-${fact.assertion_id}-type`} className={styles.recordRoleSelect}
                value={type} disabled={busy}
                onChange={(e) => changeType(e.target.value)}>
          {/* The fact's CURRENT type is always offered, even when the column would not admit it for a
              new fact — otherwise the control would silently misreport what the fact is. */}
          {!addOptions?.types.some((t) => t.relationship_type === fact.relationship_type) && (
            <option value={fact.relationship_type}>{formatRole(fact.relationship_type)}</option>
          )}
          {(addOptions?.types ?? []).map((t: CrmProjectedAddType) => (
            <option key={t.relationship_type} value={t.relationship_type}>{t.label}</option>
          ))}
        </select>
      </div>

      {retyping && (
        // §1.4's asymmetry, stated where it is being risked: changing the type mints a new fact
        // identity, so it is a refute plus a new record rather than a revision.
        <p className={styles.factNote} role="note">
          Changing the relationship replaces this record: the current one is withdrawn and a new one is
          created, linked to it.
        </p>
      )}

      {unrenderable ? (
        <p className={styles.factNote}>
          This relationship needs a field this screen cannot show yet, so its details are read-only.
          You can still remove it, or change what kind of relationship it is.
        </p>
      ) : slots.map((s) => (
        <SlotControl key={s.key} slot={s} value={draft[s.key] ?? ''} disabled={busy}
                     idPrefix={`fact-${fact.assertion_id}`}
                     onChange={(v) => setDraft((d) => ({ ...d, [s.key]: v }))} />
      ))}

      {/* WP-4 / lever C, made EDITABLE by PR-F — WHICH FIRM this person acted through on THIS fact.
          PR-C shipped the line read-only and handed the control here, because `acting_for` is an
          ENTITY_REF: the add-options contract has no slot kind for a governed picker, so the edit
          could not be expressed as a slot and had to be its own control.

          ⚠ **SET or CHANGE, never CLEAR.** These commands MERGE with the stored row and there is no
          way to send "remove this key" — `save` skips blanks for exactly that reason. Removing a
          capacity is therefore still a Remove-and-re-add, and saying so is better than a control
          that silently does nothing.

          ⚠ It is identity-bearing, so a change here is a refute-and-recreate, not a revision — the
          same warning the type switch carries, stated where the risk is taken.

          Absent key ⇒ the concept does not apply to this fact and nothing renders. */}
      {'acting_for' in fact && (
        pickingOrg && renderOrgPicker ? (
          <div className={styles.factSlot}>
            <span className={styles.factSlotLabel}>Acting for</span>
            {renderOrgPicker({
              initial: null,
              onCommit: (v: unknown) => {
                setPickingOrg(false);
                const picked = Array.isArray(v) ? v[0] : v;
                const id = (picked as { local_id?: unknown; name?: unknown } | null)?.local_id;
                if (typeof id !== 'number') return;
                const name = (picked as { name?: unknown }).name;
                setCapacity({ local_id: id, name: typeof name === 'string' ? name : null });
              },
              onCancel: () => setPickingOrg(false),
            })}
          </div>
        ) : (
          <p className={styles.factNote} role="note">
            {capacity
              ? <>Will be recorded as acting for <strong>{capacity.name ?? 'the chosen firm'}</strong>
                {' '}— saving replaces this record with a corrected one.</>
              : fact.acting_for
                ? <>Recorded as acting for <strong>{fact.acting_for.name ?? 'an unnamed firm'}</strong>.</>
                : 'No firm recorded for this person on this deal — the panel groups them by their current employer instead.'}
            {renderOrgPicker && (
              <>
                {' '}
                <button type="button" className={styles.gridMiniBtn} disabled={busy}
                        onClick={() => setPickingOrg(true)}>
                  {fact.acting_for || capacity ? 'Change the firm…' : 'Name the firm…'}
                </button>
              </>
            )}
          </p>
        )
      )}

      {removing ? (
        <div className={styles.factRemoveRow}>
          {/* ⚠ The SAME taxonomy the field rejections use, imported rather than re-listed: the UX rule
              is no free text where a vocabulary exists, and a second copy of these five values would
              drift and split the tuning signal. Inline, per §1.2, instead of a second stacked dialog —
              having nowhere to ask this is one of the three reasons this modal exists at all. */}
          <div className={styles.factSlot}>
            <label className={styles.factSlotLabel} htmlFor={`fact-${fact.assertion_id}-reason`}>
              {reasonOwed ? 'Why remove it?' : 'Why remove it? (optional)'}
            </label>
            <select id={`fact-${fact.assertion_id}-reason`} className={styles.recordRoleSelect}
                    value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}>
              <option value="">Choose…</option>
              {FIELD_REASONS.map((r) => (
                <option key={r.key} value={r.key} title={r.desc}>{r.label}</option>
              ))}
            </select>
          </div>
          <div className={styles.factSlot}>
            <label className={styles.factSlotLabel} htmlFor={`fact-${fact.assertion_id}-note`}>
              {reason === 'other' ? 'Say more (required)' : 'Say more (optional)'}
            </label>
            <input id={`fact-${fact.assertion_id}-note`} className={styles.modalInput} maxLength={300}
                   value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className={styles.modalActions}>
            <button className={styles.gridMiniBtn} disabled={busy}
                    onClick={() => { setRemoving(false); setReason(''); setNote(''); }}>Cancel</button>
            {/* ⚠ The reason is owed by the PRODUCER being corrected (owner ruling, 2026-08-07). §3's
                tuning log is "the model keeps proposing X and humans keep saying Y" — a fact a human
                authored has no model behind it, so demanding an explanation to remove your own entry
                is friction that buys nothing. Machine facts still cannot be retracted silently.
                And the button SAYS why it is disabled: a bare greyed-out Remove reads as broken. */}
            <button className={styles.gridPageBtn} disabled={busy || !canRemove} title={removeHint}
                    onClick={() => onRemove(
                      note.trim() ? `${reason}: ${note.trim()}` : reason)}>Remove</button>
          </div>
        </div>
      ) : (
        <div className={styles.modalActions}>
          {/* Offered only while there IS a decision to make: a fact a human already authored or
              confirmed would be presented as pending, and re-confirming inflates any corroboration
              count built on top of it. Same rule the row's ✓ followed. */}
          {!fact.human_decided && (
            <button className={styles.gridMiniBtn} disabled={busy} onClick={onConfirm}
                    title="Confirm — this is right as it stands">Confirm</button>
          )}
          <button className={styles.gridMiniBtn} disabled={busy} onClick={onChangeEntityClick}>
            Change who this is…
          </button>
          <button className={styles.gridMiniBtn} disabled={busy} onClick={() => setRemoving(true)}>
            Remove…
          </button>
          <button className={styles.gridPageBtn} disabled={!canSave} title={saveHint}
                  onClick={save}>Save</button>
        </div>
      )}
    </section>
  );
}

/** The add section: pick a type, fill its slots, then name the entity through the governed picker. */
function AddSection(
  { addOptions, busy, entityName, entityId, onAdd, renderRefPicker, describe }:
  { addOptions: CrmProjectedAddOptions; busy?: boolean; entityName: string | null;
    /** The chip's entity when the modal was opened on one — "add ANOTHER relationship for X". */
    entityId: number | null;
    onAdd: (type: string, localId: number, q: Draft, reason?: string) => void;
    renderRefPicker: Props['renderRefPicker'];
    /** §1.3's natural-language front door. Absent ⇒ the slot controls alone, which is the fallback. */
    describe?: DescribeIt },
) {
  const first = addOptions.types[0];
  const [type, setType] = useState(first?.relationship_type ?? '');
  const [draft, setDraft] = useState<Draft>(() => seedDraft(first?.slots ?? [], {}));
  const [picking, setPicking] = useState(false);
  // §1.3 — the sentence, and the ONE prior turn. Stateless: two turns maximum, and the second is the
  // last, so there is nowhere for a third to accumulate.
  const [sentence, setSentence] = useState('');
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState<CrmFactInterpretResponse | null>(null);
  const [pickedCandidate, setPickedCandidate] = useState<number | null>(null);

  /** Fill the CONTROLS from the draft, rather than holding it apart from them (§1.3). */
  const applyProposal = (r: CrmFactInterpretResponse) => {
    setResult(r);
    setPickedCandidate(r.proposal.entity.local_id);
    if (r.proposal.relationship_type) {
      setType(r.proposal.relationship_type);
      setDraft(seedDraft(slotsFor(addOptions, r.proposal.relationship_type),
                         r.proposal.qualifiers));
    }
  };

  const send = (answerText?: string) => {
    if (!describe || !sentence.trim()) return;
    describe.run({
      text: sentence.trim(),
      priorProposal: answerText ? result?.proposal : undefined,
      answer: answerText,
    }, applyProposal);
  };

  const slots = slotsFor(addOptions, type);
  const missingRequired = slots.some((s) => s.required && !s.fixed_value && !draft[s.key]);
  // ⛔ The 2026-08-10 ruling: an optional identity-bearing slot must be ANSWERED, not defaulted. See
  // `forcesChoice` — an operator who tabs past the side mints a fact nothing can ever fold.
  const owesChoice = unanswered(slots, draft);
  const blocked = missingRequired || owesChoice;
  const blockedHint = missingRequired ? 'Fill in every required field first'
    : owesChoice ? 'Choose a value, or “(not stated)”, for every field first' : undefined;
  // The parse resolved a DIFFERENT entity from the one this modal is scoped to (§5).
  const parsed = result?.proposal.entity;
  const mismatch = entityId != null && parsed?.local_id != null && parsed.local_id !== entityId;
  const acceptedName = parsed?.candidates.find((c) => c.local_id === pickedCandidate)?.name
    ?? (parsed?.local_id === pickedCandidate ? parsed?.name : null);

  const payloadFor = () => {
    const payload: Draft = {};
    for (const s of slots) {
      const val = s.fixed_value ?? draft[s.key] ?? '';
      if (isStated(val)) payload[s.key] = val;   // '' is "(not stated)"; UNCHOSEN cannot reach here
    }
    return payload;
  };

  const commit = (v: unknown) => {
    setPicking(false);
    // Both shapes: the governed picker commits a bare ref cell when single-select, a list otherwise.
    const picked = Array.isArray(v) ? v[0] : v;
    const id = (picked as { local_id?: unknown } | null)?.local_id;
    if (typeof id !== 'number' || blocked) return;
    onAdd(type, id, payloadFor(), result?.operator_text);
  };

  return (
    <section className={styles.factSection} aria-label="Add a relationship">
      <h4 className={styles.factSectionTitle}>
        {entityName ? `Add another relationship for ${entityName}` : 'Add a relationship'}
      </h4>

      {describe && (
        // §1.3 — one entry field, one response field, a proposal, Send, Accept. The parse FILLS the
        // controls below, so every value stays editable before Accept: an operator who disagrees
        // overrides in place rather than re-typing. Nothing is written by this box — the interpret call
        // is read-only, and Accept runs the same add the controls run.
        <div className={styles.factRemoveRow}>
          <div className={styles.factSlot}>
            <label className={styles.factSlotLabel} htmlFor="fact-describe">Describe it</label>
            <input id="fact-describe" className={styles.modalInput} maxLength={400} value={sentence}
                   disabled={busy || describe.busy}
                   placeholder={addOptions.from_kind === 'person'
                     ? 'Ray Xu is Fund Counsel to Ember'
                     : 'Raymond James is sellside investment bank'}
                   onChange={(e) => setSentence(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
          </div>
          <div className={styles.modalActions}>
            <button className={styles.gridMiniBtn}
                    disabled={busy || describe.busy || !sentence.trim()}
                    onClick={() => send()}>{describe.busy ? 'Reading…' : 'Send'}</button>
          </div>
          {/* §6: the failure is INLINE and the controls below stay usable. Never a hanging spinner. */}
          {describe.error && (
            <div className={styles.modalError} role="alert">{describe.error}</div>
          )}
          {result?.questions.length ? (
            <ul className={styles.factNote}>
              {result.questions.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          ) : null}
          {/* Ambiguity is a question with CANDIDATE CHIPS, never a ranked guess silently taken (§5). */}
          {(result?.proposal.entity.candidates.length ?? 0) > 1 && (
            <div className={styles.modalActions}>
              {result!.proposal.entity.candidates.map((c) => (
                <button key={c.local_id}
                        className={pickedCandidate === c.local_id
                          ? styles.gridPageBtn : styles.gridMiniBtn}
                        disabled={busy} onClick={() => setPickedCandidate(c.local_id)}>
                  {c.name ?? `#${c.local_id}`}
                </button>
              ))}
            </div>
          )}
          {/* The second turn answers the question WITHOUT re-typing the sentence, and it is the last. */}
          {result && result.can_ask_again && result.questions.length > 0 && (
            <div className={styles.factSlot}>
              <label className={styles.factSlotLabel} htmlFor="fact-answer">Answer</label>
              <input id="fact-answer" className={styles.modalInput} maxLength={200} value={answer}
                     disabled={busy || describe.busy}
                     onChange={(e) => setAnswer(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter' && answer.trim()) send(answer.trim()); }} />
            </div>
          )}
        </div>
      )}

      <div className={styles.factSlot}>
        {/* "New relationship", not "Relationship": the existing facts' type controls already carry that
            name, and three controls with one accessible name in a single dialog is unusable with a
            screen reader — you cannot tell which fact you are on. */}
        <label className={styles.factSlotLabel} htmlFor="fact-add-type">New relationship</label>
        <select id="fact-add-type" className={styles.recordRoleSelect} value={type} disabled={busy}
                onChange={(e) => {
                  setType(e.target.value);
                  setDraft(seedDraft(slotsFor(addOptions, e.target.value), {}));
                }}>
          {addOptions.types.map((t) => (
            <option key={t.relationship_type} value={t.relationship_type}>{t.label}</option>
          ))}
        </select>
      </div>
      {slots.map((s) => (
        <SlotControl key={s.key} slot={s} value={draft[s.key] ?? ''} disabled={busy} idPrefix="fact-add"
                     pinned onChange={(v) => setDraft((d) => ({ ...d, [s.key]: v }))} />
      ))}
      {picking ? (
        renderRefPicker({ initial: null, onCommit: commit, onCancel: () => setPicking(false) })
      ) : (
        <div className={styles.modalActions}>
          {/* Disabled until the required slots are filled: the ledger refuses a fact missing a required
              qualifier, and a button that always 400s is worse than no button. It is ALSO disabled while
              an optional identity-bearing slot is unanswered — that one the ledger would accept, and
              accepting it is the harm (owner ruling 2026-08-10; see `forcesChoice`). */}
          {mismatch ? (
            // ⚠ The sentence named someone OTHER than the row this modal was opened on. Writing it
            // against the chip's entity is the harm §5 exists to prevent — the fact hangs off the wrong
            // company and only a refute removes it — and the operator would never see the name they
            // typed. Refuse and point at the path that CAN express it.
            <p className={styles.factNote} role="note">
              That sentence names {result?.proposal.entity.name || 'someone else'}, not {entityName}.
              Use “+ Add” on the column to record a relationship for a different company or person.
            </p>
          ) : (entityId ?? pickedCandidate) != null ? (
            // ⚠ "Add ANOTHER relationship for Jefferies" already names the entity, so asking the
            // operator to search for it again is both busywork and a trap: they could pick a DIFFERENT
            // company under a heading naming this one. The picker is not offered on this path.
            <button className={styles.gridPageBtn} disabled={busy || blocked || !type}
                    title={blockedHint}
                    onClick={() => onAdd(type, (entityId ?? pickedCandidate) as number,
                                         payloadFor(), result?.operator_text)}>
              {/* ⚠ ALWAYS the name. A bare "Accept" asked the operator to confirm a governed write to an
                  entity whose identity was never on screen: a single directory match resolved silently,
                  so "Jefferies LLC" and "Jefferies Financial Group" were indistinguishable at the one
                  moment it mattered. */}
              {entityId != null
                ? `Add for ${entityName}`
                : `Accept — ${acceptedName ?? 'chosen entity'}`}
            </button>
          ) : (
            // The field-level add names the entity LAST and through the governed picker, so it cannot
            // invent one.
            <button className={styles.gridPageBtn} disabled={busy || blocked || !type}
                    title={blockedHint}
                    onClick={() => setPicking(true)}>Choose who…</button>
          )}
        </div>
      )}
    </section>
  );
}

export function CrmFactEditModal(
  { columnLabel, entryLabel, chip, addOptions, busy, error, onClose, onSaveFact, onRetypeFact,
    onChangeEntity, onRemoveFact, onConfirmFact, onAddFact, renderRefPicker, renderOrgPicker,
    describe }: Props,
) {
  const [repointing, setRepointing] = useState<CrmProjectedFact | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const name = chip ? ((chip.ref.name ?? '').trim() || '(unnamed)') : null;
  const title = name ? `${name} — on ${entryLabel}` : `Add to ${columnLabel}`;
  const facts = useMemo(() => chip?.facts ?? [], [chip]);

  // Focus moves INTO the card on open. Without it the operator's focus stays on the pencil behind the
  // dialog, Tab walks the panel underneath, and the card's own Escape handler can never fire — the
  // panel's document-level Escape listener would close the whole record panel out from under it.
  useEffect(() => { cardRef.current?.focus(); }, []);

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div ref={cardRef} className={styles.factModalCard} role="dialog" aria-modal="true"
           aria-label={title} tabIndex={-1}
           onMouseDown={(e) => e.stopPropagation()}
           onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
        <h3 className={styles.modalTitle}>{title}</h3>
        {/* Inside the card, never a page-flow banner: that renders BEHIND the backdrop. */}
        {error && <div className={styles.modalError} role="alert">{error}</div>}

        {repointing ? (
          <section className={styles.factSection} aria-label="Choose the right entity">
            <h4 className={styles.factSectionTitle}>Which {chip?.kind === 'person' ? 'person' : 'company'} is it?</h4>
            {/* Opened INSIDE this modal rather than as a second stacked one (§1.2). */}
            {renderRefPicker({
              initial: chip?.ref ?? null,
              onCommit: (v: unknown) => {
                const picked = Array.isArray(v) ? v[0] : v;
                const id = (picked as { local_id?: unknown } | null)?.local_id;
                const target = repointing;
                setRepointing(null);
                if (typeof id === 'number' && target && id !== chip?.ref.local_id) {
                  onChangeEntity(target, id);
                }
              },
              onCancel: () => setRepointing(null),
            })}
          </section>
        ) : (
          <>
            {facts.map((f) => (
              <FactSection key={f.assertion_id} fact={f} addOptions={addOptions} busy={busy}
                           onSave={(q) => onSaveFact(f, q)}
                           onRetype={(t, q) => onRetypeFact(f, t, q)}
                           onRemove={(reason) => onRemoveFact(f, reason)}
                           onConfirm={() => onConfirmFact(f)}
                           onChangeEntityClick={() => setRepointing(f)}
                           renderOrgPicker={renderOrgPicker} />
            ))}
            {addOptions && addOptions.types.length > 0 && (
              <AddSection addOptions={addOptions} busy={busy} entityName={name}
                          entityId={chip?.ref.local_id ?? null} describe={describe}
                          onAdd={onAddFact} renderRefPicker={renderRefPicker} />
            )}
            {facts.length === 0 && !addOptions && (
              <p className={styles.factNote}>Nothing on this column can be edited or added here.</p>
            )}
          </>
        )}

        <div className={styles.modalActions}>
          {/* Close is non-destructive: no pending state survives it, and nothing is written without an
              explicit Save, Remove or Add (§1.2). */}
          <button className={styles.gridMiniBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
